import {useState, useEffect} from "react";
import {useFormContext} from "react-hook-form";
import BookingDates from "../../booking/types/BookingDates";
import moment from "moment";
import useAddRoom from "../../booking/hooks/useAddRoom";
import useCacheRemove from "../../core/services/query/hooks/useCacheRemove";
import useBookingSearchContext from "../../booking/hooks/useBookingSearchContext";
import HotelRoom from "../../hotels/types/HotelRoom";
import {WizardSaveInput} from "../types/WizardSaveInput";

export interface UseRoomCardLogicProps {
  preBookingInternalId?: string;
  hotelRoom: HotelRoom;
  startDate?: string;
  endDate?: string;
}

export const useRoomCardLogic = ({
  preBookingInternalId,
  hotelRoom,
  startDate,
  endDate,
}: UseRoomCardLogicProps) => {
  const {setValue} = useFormContext<WizardSaveInput>();
  const {rooms} = useBookingSearchContext();
  const removeCache = useCacheRemove();

  // States
  const [isDateSelectorOpen, setIsDateSelectorOpen] = useState(false);
  const [selectedStartDate, setSelectedStartDate] = useState<string>(startDate || "");
  const [selectedEndDate, setSelectedEndDate] = useState<string>(endDate || "");
  const [quantity, setQuantity] = useState(1);
  const [overbooking, setOverbooking] = useState(false);
  const [available, setAvailable] = useState(true);
  const [hotelAvailable, setHotelAvailable] = useState(true);
  const [roomAdded, setRoomAdded] = useState(false);
  const [unavailableDates, setUnavailableDates] = useState<BookingDates[]>([]);
  const [overbookingDates, setOverbookingDates] = useState<BookingDates[]>([]);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const [isCheckingAvailability, setIsCheckingAvailability] = useState(false);

  // Hooks
  const {save: addRoom, error: addRoomError, isLoading: isAddingRoomLoading} = useAddRoom();

  // Calculate suggested quantity based on guest capacity
  useEffect(() => {
    let totalCapacity = hotelRoom.adults + hotelRoom.children;
    let suggestedQuantity = 0;

    rooms.forEach((room) => {
      let totalChildren = room.guestChildren || 0;
      let totalAdults = room.guestAdults || 0;
      let requestQuantity = totalChildren + totalAdults;

      if (requestQuantity === totalCapacity) {
        suggestedQuantity = suggestedQuantity + 1;
      }
    });

    if (suggestedQuantity === 0) {
      suggestedQuantity = 1;
    }

    setQuantity(suggestedQuantity);
  }, [rooms, hotelRoom.adults, hotelRoom.children]);

  // Handle opening date selector modal
  const handleOpenDateSelector = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDateSelectorOpen(true);
  };

  // Handle dates confirmed from modal
  const handleDatesConfirmed = async (startDate: string, endDate: string) => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setIsDateSelectorOpen(false);
    setIsCheckingAvailability(true);
    await handleAddRoom(false, startDate, endDate);
  };

  // Add room logic
  const handleAddRoom = async (allowOverbooking: boolean = false, startDate?: string, endDate?: string) => {
    setModalIsOpen(false);

    const arrivalDate = startDate || selectedStartDate;
    const departureDate = endDate || selectedEndDate;

    addRoom(
      {
        prebookingId: preBookingInternalId,
        prebooking: '/web/prebookings/' + preBookingInternalId,
        roomHotelEvent: '/web/room_hotel_events/' + hotelRoom.id,
        arrivalDate: moment(arrivalDate).format('YYYY-MM-DD'),
        departureDate: moment(departureDate).format('YYYY-MM-DD'),
        quantity: quantity,
        allowOverbooking: allowOverbooking
      },
      {
        onSuccess: () => {
          setRoomAdded(true);
          setOverbooking(false);
          setAvailable(true);
          setUnavailableDates([]);
          setOverbookingDates([]);
          setModalIsOpen(true);
          setIsCheckingAvailability(false);
          setValue('selectedRoomId', hotelRoom.id.toString());
          removeCache('basket-items');
        },
        onError: (error: any) => {
          if (error.statusCode === 422) {
            const prebookingHotelViolation = error.content.violations.find((v: any) => v.propertyPath === 'prebookingHotel');
            const availableViolation = error.content.violations.find((v: any) => v.propertyPath === 'available');

            if (prebookingHotelViolation !== undefined) {
              setHotelAvailable(false);
              setRoomAdded(false);
              setModalIsOpen(true);
              setIsCheckingAvailability(false);
            } else if (availableViolation !== undefined) {
              setAvailable(availableViolation.message === 'true');
              const overbookingViolation = error.content.violations.find((v: any) => v.propertyPath === 'overbooking');
              setOverbooking(overbookingViolation?.message === 'true');

              const unavailableDatesViolation = error.content.violations.find((v: any) => v.propertyPath === 'unavailableDates');
              const arrayUnavailableDates = unavailableDatesViolation?.message ?
                unavailableDatesViolation.message.split(',').map((dateStr: string) => ({fecha: dateStr})) : [];
              setUnavailableDates(arrayUnavailableDates);

              const overbookingDatesViolation = error.content.violations.find((v: any) => v.propertyPath === 'overbookingDates');
              const arrayOverbookingDates = overbookingDatesViolation?.message ?
                overbookingDatesViolation.message.split(',').map((dateStr: string) => ({fecha: dateStr})) : [];
              setOverbookingDates(arrayOverbookingDates);

              setRoomAdded(false);
              setModalIsOpen(true);
              setIsCheckingAvailability(false);
            } else {
              error.content.violations.forEach((v: any) => {
                setErrorMessage(v.message);
              });
              setRoomAdded(false);
              setOverbooking(false);
              setAvailable(true);
              setUnavailableDates([]);
              setOverbookingDates([]);
              setModalIsOpen(true);
            }
          } else {
            setRoomAdded(false);
            setOverbooking(false);
            setAvailable(true);
            setUnavailableDates([]);
            setOverbookingDates([]);
            setModalIsOpen(true);
          }
        }
      }
    );
  };

  // Confirm add room with overbooking
  const handleConfirmAddRoom = async () => {
    setIsCheckingAvailability(true);
    await handleAddRoom(true, selectedStartDate, selectedEndDate);
  };

  // Delete room logic - MOVED TO HotelPackageRoomCard

  // Determine modal kind
  let modalKind: "roomAdded" | "roomNotAvailable" | "roomAvailableWithOverbooking" | "hotelNotAvailable" | "error" | null = null;

  if (roomAdded) {
    modalKind = 'roomAdded';
  } else {
    if (!available) {
      modalKind = 'roomNotAvailable';
    } else if (overbooking) {
      modalKind = 'roomAvailableWithOverbooking';
    } else if (!hotelAvailable) {
      modalKind = 'hotelNotAvailable';
    } else if (addRoomError) {
      modalKind = 'error';
    }
  }

  return {
    // States
    isDateSelectorOpen,
    setIsDateSelectorOpen,
    selectedStartDate,
    selectedEndDate,
    quantity,
    setQuantity,
    overbooking,
    available,
    hotelAvailable,
    roomAdded,
    unavailableDates,
    overbookingDates,
    modalIsOpen,
    setModalIsOpen,
    errorMessage,
    isCheckingAvailability,

    // API states
    isAddingRoomLoading,
    addRoomError,

    // Handlers
    handleOpenDateSelector,
    handleDatesConfirmed,
    handleAddRoom,
    handleConfirmAddRoom,

    // Computed
    modalKind,
  };
};

